interface OrderTimelineProps {
  estado: string;
  createdAt: string;
  trackingNumber?: string | null;
  uspsTrackingNumber?: string | null;
}

type Step = {
  id: string;
  label: string;
  sublabel: string;
  icon: string;
};

const STEPS: Step[] = [
  { id: 'pagado', label: 'Pago Confirmado', sublabel: 'Tu pago fue recibido', icon: 'ri-checkbox-circle-line' },
  { id: 'enviado', label: 'En Camino', sublabel: 'El vendedor envió tu paquete', icon: 'ri-truck-line' },
  { id: 'entregado', label: 'Entregado', sublabel: '¡Tu pedido llegó!', icon: 'ri-home-smile-line' },
];

const ESTADO_INDEX: Record<string, number> = {
  pendiente: -1,
  pagado: 0,
  enviado: 1,
  entregado: 2,
  completado: 2,
  cancelado: -2,
};

export default function OrderTimeline({ estado, createdAt, trackingNumber, uspsTrackingNumber }: OrderTimelineProps) {
  const currentIndex = ESTADO_INDEX[estado] ?? 0;
  const isCancelled = estado === 'cancelado';
  const tracking = uspsTrackingNumber || trackingNumber;

  if (isCancelled) {
    return (
      <div className="mt-4 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
        <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-full shrink-0">
          <i className="ri-close-circle-line text-red-500 text-lg"></i>
        </div>
        <div>
          <p className="text-sm font-semibold text-red-700">Orden Cancelada</p>
          <p className="text-xs text-red-500">Si realizaste un pago, el reembolso se procesará en 3-5 días hábiles.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      {/* Barra de progreso */}
      <div className="relative flex items-center justify-between mb-2">
        {/* Línea de fondo */}
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-gray-200 rounded-full z-0 mx-6"></div>
        {/* Línea de progreso */}
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-gray-900 rounded-full z-0 mx-6 transition-all duration-700"
          style={{ width: currentIndex === 0 ? '0%' : currentIndex === 1 ? '50%' : '100%' }}
        ></div>

        {STEPS.map((step, idx) => {
          const done = idx <= currentIndex;
          const active = idx === currentIndex;
          return (
            <div key={step.id} className="relative z-10 flex flex-col items-center gap-1.5 flex-1">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                  done
                    ? active
                      ? 'bg-gray-900 border-gray-900 text-white scale-110'
                      : 'bg-gray-900 border-gray-900 text-white'
                    : 'bg-white border-gray-300 text-gray-400'
                }`}
              >
                {done && !active ? (
                  <i className="ri-check-line text-sm font-bold"></i>
                ) : (
                  <i className={`${step.icon} text-sm`}></i>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Labels */}
      <div className="flex items-start justify-between">
        {STEPS.map((step, idx) => {
          const done = idx <= currentIndex;
          const active = idx === currentIndex;
          return (
            <div key={step.id} className="flex-1 text-center px-1">
              <p className={`text-xs font-semibold leading-tight ${done ? 'text-gray-900' : 'text-gray-400'} ${active ? 'font-bold' : ''}`}>
                {step.label}
              </p>
              {active && (
                <p className="text-xs text-gray-500 mt-0.5 leading-tight hidden sm:block">{step.sublabel}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Tracking USPS */}
      {tracking && currentIndex >= 1 && (
        <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-gray-900 rounded-lg shrink-0">
              <i className="ri-barcode-line text-white text-xs"></i>
            </div>
            <div>
              <p className="text-xs text-gray-500 leading-none mb-0.5">Número de Tracking</p>
              <p className="text-xs font-mono font-bold text-gray-900">{tracking}</p>
            </div>
          </div>
          <a
            href={
              uspsTrackingNumber
                ? `https://tools.usps.com/go/TrackConfirmAction?tLabels=${uspsTrackingNumber}`
                : `https://www.islandwide.com/tracking?number=${tracking}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 text-white text-xs font-bold rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-external-link-line"></i>
            Rastrear Paquete
          </a>
        </div>
      )}

      {/* Fecha de compra */}
      <p className="text-xs text-gray-400 mt-2 text-right">
        Comprado el {new Date(createdAt).toLocaleDateString('es-PR', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>
    </div>
  );
}
